import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { CheckCircle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { useJira } from '../../../core/hooks/useJira';
import { LoadingState } from '../../../components/shared/LoadingState';

export const JiraSettings: React.FC = () => {
  const {
    config,
    loading,
    saveConfig,
    initiateOAuthFlow,
    handleOAuthCallback,
    getAccessibleSites,
    selectSite,
    disconnect,
  } = useJira();

  const [sites, setSites] = useState<any[]>([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [saving, setSaving] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      clientId: config?.clientId || '',
      clientSecret: config?.clientSecret || '',
      redirectUri: config?.redirectUri || `${window.location.origin}/settings`,
    },
  });

  const handleOAuth = useCallback(
    async (code: string) => {
      try {
        await handleOAuthCallback(code);
        // Clear URL params
        window.history.replaceState({}, document.title, window.location.pathname);
        alert('Successfully connected to Jira!');
      } catch (error) {
        console.error('OAuth error:', error);
        alert('Failed to connect to Jira. Please try again.');
      }
    },
    [handleOAuthCallback]
  );

  useEffect(() => {
    // Check if returning from OAuth
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (code && state) {
      handleOAuth(code);
    }
  }, [handleOAuth]);

  useEffect(() => {
    if (config) {
      reset({
        clientId: config.clientId,
        clientSecret: config.clientSecret,
        redirectUri: config.redirectUri,
      });
    }
  }, [config, reset]);

  const onSave = async (data: any) => {
    try {
      setSaving(true);
      await saveConfig({
        ...config,
        ...data,
      });
      alert('Jira configuration saved!');
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleConnect = () => {
    try {
      const authUrl = initiateOAuthFlow();
      window.location.href = authUrl;
    } catch (error) {
      console.error('Connect error:', error);
      alert('Failed to initiate OAuth flow');
    }
  };

  const loadSites = async () => {
    try {
      setLoadingSites(true);
      const accessibleSites = await getAccessibleSites();
      setSites(accessibleSites);
    } catch (error) {
      console.error('Load sites error:', error);
      alert('Failed to load Jira sites');
    } finally {
      setLoadingSites(false);
    }
  };

  const handleSelectSite = async (site: any) => {
    try {
      await selectSite(site);
      alert('Site selected successfully!');
      setSites([]);
    } catch (error) {
      console.error('Select site error:', error);
      alert('Failed to select site');
    }
  };

  const handleDisconnect = async () => {
    if (window.confirm('Are you sure you want to disconnect Jira?')) {
      try {
        await disconnect();
        alert('Disconnected from Jira');
      } catch (error) {
        console.error('Disconnect error:', error);
        alert('Failed to disconnect');
      }
    }
  };

  if (loading) {
    return <LoadingState message="Loading Jira settings..." />;
  }

  return (
    <div className="space-y-6">
      {/* Connection Status */}
      {config?.accessToken && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <CardTitle>Connected to Jira</CardTitle>
              </div>
              <Button variant="destructive" onClick={handleDisconnect}>
                Disconnect
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {config.baseUrl && (
                <div>
                  <p className="text-sm text-muted-foreground">Jira Site</p>
                  <p className="font-medium">{config.baseUrl}</p>
                </div>
              )}
              {config.cloudId && (
                <div>
                  <p className="text-sm text-muted-foreground">Cloud ID</p>
                  <p className="font-medium">{config.cloudId}</p>
                </div>
              )}

              {!config.cloudId && (
                <div className="space-y-2">
                  <Button onClick={loadSites} disabled={loadingSites}>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    {loadingSites ? 'Loading...' : 'Select Jira Site'}
                  </Button>

                  {sites.length > 0 && (
                    <div className="space-y-2 mt-4">
                      <p className="text-sm font-medium">Available Sites:</p>
                      {sites.map((site) => (
                        <Card key={site.id} className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{site.name}</p>
                              <p className="text-sm text-muted-foreground">{site.url}</p>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => handleSelectSite(site)}
                            >
                              Select
                            </Button>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* OAuth Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>OAuth Configuration</CardTitle>
          <CardDescription>
            Configure your Jira OAuth 2.0 credentials.{' '}
            <a
              href="https://developer.atlassian.com/cloud/jira/platform/oauth-2-3lo-apps/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Learn how to set up OAuth 2.0
            </a>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSave)} className="space-y-4">
            <Input
              label="Client ID"
              {...register('clientId', { required: 'Client ID is required' })}
              error={errors.clientId?.message}
            />

            <Input
              label="Client Secret"
              type="password"
              {...register('clientSecret', { required: 'Client Secret is required' })}
              error={errors.clientSecret?.message}
            />

            <Input
              label="Redirect URI"
              {...register('redirectUri', { required: 'Redirect URI is required' })}
              error={errors.redirectUri?.message}
              placeholder="https://your-app.com/settings"
            />

            <div className="flex justify-end gap-2">
              <Button type="submit" variant="outline" loading={saving}>
                Save Configuration
              </Button>
              <Button
                type="button"
                onClick={handleConnect}
                disabled={!config?.clientId || !config?.clientSecret}
              >
                Authenticate with Jira
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
